import {Box} from '@dropbox/dig-foundations';
import {EmployeeService, NewsService} from 'client';
import {TeamCreate} from 'components/teams/TeamCreate';
import {TeamEdit} from 'components/teams/TeamEdit';
import {ROUTE_PATHS, STEAM_TEAM_SLUG} from 'constant';
import {CardManagement} from 'pages/CardManagement';
import {CeleryTaskMetaAdmin} from 'pages/CeleryTaskMeta';
import {ErrorPage} from 'pages/ErrorPage';
import {Feedback} from 'pages/Feedback';
import {Gratitude} from 'pages/Gratitude';
import {Knowledge, KnowledgeGuide, KnowledgeHome} from 'pages/Knowledge';
import {Logout} from 'pages/Logout';
import {ManageBadges} from 'pages/ManageBadges';
import {News} from 'pages/News';
import {NotFound} from 'pages/NotFound';
import {PeopleRoute} from 'pages/PeopleRoute';
import {Settings} from 'pages/Settings';
import {TeamApps} from 'pages/TeamApps';
import {TeamOwnership} from 'pages/TeamOwnership';
import {Teams} from 'pages/Teams';
import {Waitlist} from 'pages/Waitlist';
import {Suspense} from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  data,
  LoaderFunction,
  LoaderFunctionArgs,
  Navigate,
  redirect,
  Route,
} from 'react-router';
import {getService, getTeamsService} from 'utilities';
import {Admin} from 'views/admin/Admin';
import {AdminAIPrompt} from 'views/admin/AdminAIPrompt';
import {AdminAiView} from 'views/admin/AdminAiView';
import {AdminAmplitudeLookup} from 'views/admin/AdminAmplitudeLookup';
import {AdminAskHealth} from 'views/admin/AdminAskHealth';
import {AdminContextEngine} from 'views/admin/AdminContextEngine';
import {AdminDcdJobFamilies} from 'views/admin/AdminDcdJobFamilies';
import {AdminDocuments} from 'views/admin/AdminDocuments';
import {AdminKnowledgeLinks} from 'views/admin/AdminKnowledgeLinks';
import {AdminMigrations} from 'views/admin/AdminMigrations';
import {AdminOverdueOutcomeActionItems} from 'views/admin/AdminOverdueOutcomeActionItems';
import {AdminPillars} from 'views/admin/AdminPillars';
import {AdminRoadmapJiraGroupMappings} from 'views/admin/AdminRoadmapJiraGroupMappings';
import {AdminRuntime} from 'views/admin/AdminRuntime';
import {AdminScorecardMetricDirections} from 'views/admin/AdminScorecardMetricDirections';
import {AdminTimeframeAssignment} from 'views/admin/AdminTimeframeAssignment';
import {AdminTimeframes} from 'views/admin/AdminTimeframes';
import {AdminWireframeData} from 'views/admin/AdminWireframeData';
import {AdminWorkstreamAuditLog} from 'views/admin/AdminWorkstreamAuditLog';
import {AdminWorkstreamAutoArchive} from 'views/admin/AdminWorkstreamAutoArchive';
import {AdminWorkstreamAutodraft} from 'views/admin/AdminWorkstreamAutodraft';
import {AdminWorkstreamDraftPolicy} from 'views/admin/AdminWorkstreamDraftPolicy';
import {AdminWorkstreamImporter} from 'views/admin/AdminWorkstreamImporter';
import {AdminWorkstreamOrphans} from 'views/admin/AdminWorkstreamOrphans';
import {AdminWorkstreamReadiness} from 'views/admin/AdminWorkstreamReadiness';
import {AdminWorkstreamUpdates} from 'views/admin/AdminWorkstreamUpdates';
import {ConfidentialMetrics} from 'views/admin/ConfidentialMetrics';
import {Delegates} from 'views/admin/Delegates';
import {ExternalApp} from 'views/admin/ExternalApp';
import {AdminHomeBanners} from 'views/admin/homeBanners/AdminHomeBanners';
import {NotificationScheduler} from 'views/admin/NotificationScheduler';
import {
  AppsRoute,
  AppStudioRoute,
  CreateAppRoute,
  NovaBuilderRoute,
  UpdateAppRoute,
} from 'views/apps/AppsRoute';
import {DSYS} from 'views/dsys/DSYS';
import {InboxOutboxView} from 'views/gratitude/InboxOutboxView';
import {LeaderboardView} from 'views/gratitude/LeaderboardView';
import {Ask} from 'views/home/Ask';
import {Home} from 'views/home/Home';
import {LoggedInPage} from 'views/LoggedInPage';
import {NewsCreateView} from 'views/news/NewsCreateView';
import {NewsUpdateView} from 'views/news/NewsUpdateView';
import {PaperPreview} from 'views/paper/PaperPreview';
import {PlanningDoc} from 'views/planning/PlanningDoc';
import {ProductRoadmap} from 'views/productRoadmap/ProductRoadmap';
import {CompanyOSProView} from 'views/profile/CompanyOSProView';
import {ProfileView} from 'views/profile/ProfileView';
import {queryClient} from 'views/QueryClientWrapper';
import {RoadmapRoute} from 'views/roadmap';
import {Scorecards} from 'views/scorecards/Scorecards';
import {SearchApp} from 'views/searchApp/SearchApp';
import {CheckupTool} from 'views/tools/CheckupTool';
import {WorkstreamsPage} from 'views/workstreams/page/WorkstreamsPage';
import {WorkstreamCreate} from 'views/workstreams/WorkstreamCreate';
import {WorkstreamEditor} from 'views/workstreams/WorkstreamEdit';

const profileLoader: LoaderFunction = async ({params}: LoaderFunctionArgs) => {
  const ldap = params.userid || '';
  return queryClient
    .fetchQuery({
      queryKey: ['profile', ldap],
      queryFn: () => getService(EmployeeService).getProfileApiV1PeopleUserIdGet(ldap),
    })
    .then((response) => {
      return response;
    })
    .catch(() => {
      return null;
    });
};

const teamLoader: LoaderFunction = async ({params}: LoaderFunctionArgs) => {
  const slug = params.slug ? params.slug : STEAM_TEAM_SLUG.toString();
  return queryClient
    .fetchQuery({
      queryKey: ['team', slug],
      queryFn: () => getTeamsService().getTeamBySlugApiV1TeamsSlugSlugGet(slug),
    })
    .then((response) => {
      return response;
    })
    .catch(() => {
      return null;
    });
};

const workstreamRedirectLoader: LoaderFunction = async ({params, request}: LoaderFunctionArgs) => {
  const {id} = params;
  if (!id) {
    throw new Response('Not Found', {status: 404});
  }

  const requestUrl = new URL(request.url);
  return redirect(`${ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', id)}${requestUrl.search}`);
};

const checkupLoader: LoaderFunction = async ({params}: LoaderFunctionArgs) => {
  return params.id ? params.id : null;
};

const newsLoader: LoaderFunction = async ({params}: LoaderFunctionArgs) => {
  try {
    if (params.identifier && /^\d+$/.test(params.identifier)) {
      const id = parseInt(params.identifier, 10);
      return await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['news', 'latest'],
          queryFn: () => getService(NewsService).getPostsApiV1NewsGet(),
        }),
        queryClient.fetchQuery({
          queryKey: ['news', id],
          queryFn: () => getService(NewsService).getPostApiV1NewsIdGet(id),
        }),
      ]);
    } else if (params.identifier) {
      if (params.identifier === 'drafts') {
        return await Promise.all([
          queryClient.fetchQuery({
            queryKey: ['news', 'drafts'],
            queryFn: () => getService(NewsService).getDraftsApiV1NewsDraftsGet(),
          }),
        ]);
      }
      if (params.identifier === 'categories') {
        return await Promise.all([
          queryClient.fetchQuery({
            queryKey: ['news', 'categories'],
            queryFn: () => getService(NewsService).getCategoriesApiV1NewsCategoriesGet(),
          }),
        ]);
      }
      return await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['news', 'category', params.identifier],
          queryFn: () =>
            getService(NewsService).getByCategoryApiV1NewsCategoryCategoryGet(
              params.identifier ?? ''
            ),
        }),
      ]);
    } else {
      return await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['news', 'latest'],
          queryFn: () => getService(NewsService).getPostsApiV1NewsGet(),
        }),
      ]);
    }
  } catch (error: any) {
    if (error.status === 401) {
      throw data(
        {
          error: 'Not Found',
        },
        {status: 401}
      );
    } else if (error.status === 404) {
      throw data(
        {
          error: 'Not Found',
        },
        {status: 404}
      );
    }
    return [[]];
  }
};

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        element={
          <Suspense fallback={null}>
            <LoggedInPage />
          </Suspense>
        }
        ErrorBoundary={ErrorPage}
      >
        <Route path={ROUTE_PATHS.LOGIN} element={<Navigate to={ROUTE_PATHS.HOME} replace />} />
        <Route
          path={ROUTE_PATHS.ME}
          loader={profileLoader}
          element={<Navigate to={ROUTE_PATHS.PROFILE} replace />}
        />
        <Route path={ROUTE_PATHS.LOGOUT} element={<Logout />} />
        <Route path={ROUTE_PATHS.HOME} element={<Home />} />
        <Route path={ROUTE_PATHS.APPS} element={<AppsRoute />} />
        <Route path={ROUTE_PATHS.APPS_NEW} element={<CreateAppRoute />} />
        <Route path={ROUTE_PATHS.APPS_STUDIO} element={<AppStudioRoute />} />
        <Route path={ROUTE_PATHS.APPS_BUILD} element={<NovaBuilderRoute />} />
        <Route path={ROUTE_PATHS.APPS_DRAFT} element={<NovaBuilderRoute />} />
        <Route path={ROUTE_PATHS.APPS_UPDATE} element={<UpdateAppRoute />} />
        <Route path={ROUTE_PATHS.ASK} element={<Ask />} />
        <Route path={ROUTE_PATHS.SEARCH_APP} element={<SearchApp />} />
        <Route path={ROUTE_PATHS.PEOPLE} element={<PeopleRoute />} />
        <Route path={ROUTE_PATHS.PROFILE} loader={profileLoader} element={<ProfileView />} />
        <Route path={ROUTE_PATHS.APRIL_FOOLS_COMPANY_OS_PRO} element={<CompanyOSProView />} />
        <Route path={ROUTE_PATHS.TEAMS} loader={teamLoader} element={<Teams />} />
        <Route path={ROUTE_PATHS.TEAMS_APPS} element={<TeamApps />} />
        <Route path={ROUTE_PATHS.TEAMS_APP} element={<TeamApps />} />
        <Route
          path={ROUTE_PATHS.TEAMS_OWNERSHIP}
          element={<Navigate to={ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES} replace />}
        />
        <Route path={ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES} element={<TeamOwnership />} />
        <Route
          path={ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES_COMPONENT}
          element={<TeamOwnership />}
        />
        <Route
          path={ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES_REFERENCE}
          element={<TeamOwnership />}
        />
        <Route
          path={ROUTE_PATHS.TEAMS_COMPONENTS_AND_SERVICES_SERVICE}
          element={<TeamOwnership />}
        />
        <Route path={ROUTE_PATHS.TEAM} loader={teamLoader} element={<Teams />} />
        <Route path={ROUTE_PATHS.TEAM_EDIT} loader={teamLoader} element={<TeamEdit />} />
        <Route path={ROUTE_PATHS.TEAM_NEW} element={<TeamCreate />} />
        <Route
          path={ROUTE_PATHS.CHECKUP_PEOPLE_ID}
          loader={checkupLoader}
          element={<CheckupTool />}
        />
        <Route
          path={ROUTE_PATHS.CHECKUP_TEAM_ID}
          loader={checkupLoader}
          element={<CheckupTool />}
        />
        <Route path={ROUTE_PATHS.SETTINGS} element={<Settings />} />
        <Route path={ROUTE_PATHS.PAPER_PREVIEW} element={<PaperPreview />} />
        <Route path={ROUTE_PATHS.FEEDBACK} element={<Feedback />} />
        <Route path={ROUTE_PATHS.FEEDBACK_ALL} element={<Feedback />} />
        <Route path={ROUTE_PATHS.PLANNING_DOC} element={<PlanningDoc />} />
        <Route path={ROUTE_PATHS.GRATITUDE_BROWSE} element={<Gratitude />} />
        <Route path={ROUTE_PATHS.GRATITUDE_INBOX} element={<InboxOutboxView mailbox="inbox" />} />
        <Route path={ROUTE_PATHS.GRATITUDE_OUTBOX} element={<InboxOutboxView mailbox="outbox" />} />
        <Route path={ROUTE_PATHS.GRATITUDE_LEADERBOARD} element={<LeaderboardView />} />
        <Route path={ROUTE_PATHS.ADMIN_CELERY_TASKS} element={<CeleryTaskMetaAdmin />} />
        <Route path={ROUTE_PATHS.ADMIN_MANAGE_BADGES} element={<ManageBadges />} />
        <Route path={ROUTE_PATHS.ADMIN_APPS} element={<ExternalApp />} />
        <Route path={ROUTE_PATHS.ADMIN_CARD_MANAGEMENT} element={<CardManagement />} />
        <Route path={ROUTE_PATHS.ADMIN_DELEGATES} element={<Delegates />} />
        <Route path={ROUTE_PATHS.ADMIN_CONFIDENTIAL_METRICS} element={<ConfidentialMetrics />} />
        <Route path={ROUTE_PATHS.ADMIN_NOTIFICATIONS} element={<NotificationScheduler />} />
        <Route
          path={ROUTE_PATHS.ADMIN_OVERDUE_OUTCOMES}
          element={<AdminOverdueOutcomeActionItems />}
        />
        <Route path={ROUTE_PATHS.ADMIN_DOCUMENTS} element={<AdminDocuments />} />
        <Route path={ROUTE_PATHS.ADMIN_KNOWLEDGE_LINKS} element={<AdminKnowledgeLinks />} />
        <Route path={ROUTE_PATHS.ADMIN_HOME_BANNERS} element={<AdminHomeBanners />} />
        <Route path={ROUTE_PATHS.ADMIN_PILLARS} element={<AdminPillars />} />
        <Route path={ROUTE_PATHS.ADMIN_TIMEFRAMES} element={<AdminTimeframes />} />
        <Route
          path={ROUTE_PATHS.ADMIN_TIMEFRAME_ASSIGNMENT}
          element={<AdminTimeframeAssignment />}
        />
        <Route
          path={ROUTE_PATHS.ADMIN_ROADMAP_JIRA_GROUP_MAPPINGS}
          element={<AdminRoadmapJiraGroupMappings />}
        />
        <Route
          path={ROUTE_PATHS.ADMIN_SCORECARD_METRIC_DIRECTIONS}
          element={<AdminScorecardMetricDirections />}
        />
        <Route
          path={ROUTE_PATHS.ADMIN_WORKSTREAM_AUDIT_LOG}
          element={<AdminWorkstreamAuditLog />}
        />
        <Route
          path={ROUTE_PATHS.ADMIN_WORKSTREAM_AUTO_ARCHIVE}
          element={<AdminWorkstreamAutoArchive />}
        />
        <Route
          path={ROUTE_PATHS.ADMIN_WORKSTREAM_AUTODRAFT}
          element={<AdminWorkstreamAutodraft />}
        />
        <Route
          path={ROUTE_PATHS.ADMIN_WORKSTREAM_DRAFT_POLICY}
          element={<AdminWorkstreamDraftPolicy />}
        />
        <Route path={ROUTE_PATHS.ADMIN_WORKSTREAM_IMPORTER} element={<AdminWorkstreamImporter />} />
        <Route path={ROUTE_PATHS.ADMIN_WORKSTREAM_ORPHANS} element={<AdminWorkstreamOrphans />} />
        <Route
          path={ROUTE_PATHS.ADMIN_WORKSTREAM_READINESS}
          element={<AdminWorkstreamReadiness />}
        />
        <Route path={ROUTE_PATHS.ADMIN_WORKSTREAM_UPDATES} element={<AdminWorkstreamUpdates />} />
        <Route path={ROUTE_PATHS.ADMIN_AI_PROMPT} element={<AdminAIPrompt />} />
        <Route path={ROUTE_PATHS.ADMIN_CONTEXT_ENGINE} element={<AdminContextEngine />} />
        <Route path={ROUTE_PATHS.ADMIN_AI_VIEW} element={<AdminAiView />} />
        <Route path={ROUTE_PATHS.ADMIN_AMPLITUDE_LOOKUP} element={<AdminAmplitudeLookup />} />
        <Route path={ROUTE_PATHS.ADMIN_ASK_HEALTH} element={<AdminAskHealth />} />
        <Route path={ROUTE_PATHS.ADMIN_MIGRATIONS} element={<AdminMigrations />} />
        <Route path={ROUTE_PATHS.ADMIN_RUNTIME} element={<AdminRuntime />} />
        <Route path={ROUTE_PATHS.ADMIN_DCD_JOB_FAMILIES} element={<AdminDcdJobFamilies />} />
        <Route path={ROUTE_PATHS.ADMIN_WIREFRAME_DATA} element={<AdminWireframeData />} />
        <Route path={ROUTE_PATHS.DSYS} element={<DSYS />} />
        <Route path={ROUTE_PATHS.NEWS} loader={newsLoader} element={<News />} />
        <Route path={ROUTE_PATHS.NEWS_POST} loader={newsLoader} element={<News />} />
        <Route path={ROUTE_PATHS.NEWS_NEW} loader={newsLoader} element={<NewsCreateView />} />
        <Route path={ROUTE_PATHS.NEWS_EDIT} loader={newsLoader} element={<NewsUpdateView />} />
        <Route path={ROUTE_PATHS.KNOWLEDGE} element={<KnowledgeHome />} />
        <Route
          path={ROUTE_PATHS.KNOWLEDGE_GUIDES}
          element={<Navigate to={ROUTE_PATHS.KNOWLEDGE} replace />}
        />
        <Route path={ROUTE_PATHS.KNOWLEDGE_GUIDE} element={<KnowledgeGuide />} />
        <Route path={ROUTE_PATHS.KNOWLEDGE_CATEGORY} element={<Knowledge />} />
        <Route path={ROUTE_PATHS.KNOWLEDGE_PAGE} element={<Knowledge />} />
        <Route path={ROUTE_PATHS.ROADMAP} element={<RoadmapRoute />} />
        <Route path={ROUTE_PATHS.ROADMAP_PRODUCT} element={<ProductRoadmap />} />
        <Route path={ROUTE_PATHS.SCORECARDS} element={<Scorecards />} />
        <Route path={ROUTE_PATHS.SCORECARDS_DATE} element={<Scorecards />} />
        <Route path={ROUTE_PATHS.WORKSTREAMS_PAGE} element={<WorkstreamsPage />} />
        <Route path={ROUTE_PATHS.WORKSTREAM_NEW} element={<WorkstreamCreate />} />
        <Route path={ROUTE_PATHS.WORKSTREAM} loader={workstreamRedirectLoader} />
        <Route path={ROUTE_PATHS.WORKSTREAM_EDIT} element={<WorkstreamEditor />} />
        <Route path={ROUTE_PATHS.ADMIN} element={<Admin />} />
      </Route>
      <Route path={ROUTE_PATHS.WAITLIST} element={<Waitlist />}></Route>
      <Route
        path="*"
        element={
          <Box style={{height: '100vh'}}>
            <NotFound />
          </Box>
        }
      />
    </>
  )
);
